import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Paper,
  styled,
  Slide,
  useTheme,
  useMediaQuery,
  Avatar,
  Fade
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, AccountCircle } from '@mui/icons-material';
import { useFormik } from 'formik';
import { accountSchema } from '../../utils/validationSchema';

interface AccountFormProps {
  onSubmit: (values: { email: string; password: string; confirmPassword: string }) => void;
  initialValues: {
    email: string;
    password: string;
    confirmPassword: string;
  };
}

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    transition: 'all 0.3s ease-in-out',
    borderRadius: 12,
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.light,
      },
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: 2,
        borderColor: theme.palette.primary.main,
      },
    },
  },
  '& .MuiInputLabel-root': {
    transition: 'all 0.3s ease',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
  '& .MuiInputAdornment-root': {
    '& .MuiSvgIcon-root': {
      color: theme.palette.action.active,
    },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginRight: 0,
    fontWeight: 500,
  },
}));

const IconWrapper = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  width: 56,
  height: 56,
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
}));

const AccountForm: React.FC<AccountFormProps> = ({ onSubmit, initialValues }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: accountSchema,
    onSubmit: (values) => {
      onSubmit(values);
    }
  });

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: '100%', maxWidth: 500, mx: 'auto', mt: isMobile ? 0 : 2 }}>
      <Fade in={true} timeout={400}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <IconWrapper>
            <AccountCircle sx={{ fontSize: 32 }} />
          </IconWrapper>
          <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
            Tạo tài khoản của bạn
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cung cấp thông tin đăng nhập để bắt đầu
          </Typography>
        </Box>
      </Fade>

      <StyledTextField
        fullWidth
        id="email"
        name="email"
        label="Email"
        placeholder="Nhập email của bạn"
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.email && Boolean(formik.errors.email)}
        helperText={formik.touched.email && formik.errors.email}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email color="action" />
            </InputAdornment>
          ),
        }}
      />

      <StyledTextField
        fullWidth
        id="password"
        name="password"
        label="Mật khẩu"
        placeholder="Tạo mật khẩu mới"
        type={showPassword ? 'text' : 'password'}
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.password && Boolean(formik.errors.password)}
        helperText={formik.touched.password && formik.errors.password}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="Hiển thị/ẩn mật khẩu"
                onClick={handleClickShowPassword}
                edge="end"
                sx={{ color: showPassword ? 'primary.main' : 'action.active' }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <StyledTextField
        fullWidth
        id="confirmPassword"
        name="confirmPassword"
        label="Xác nhận mật khẩu"
        placeholder="Nhập lại mật khẩu"
        type={showConfirmPassword ? 'text' : 'password'}
        value={formik.values.confirmPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
        helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="Hiển thị/ẩn xác nhận mật khẩu"
                onClick={handleClickShowConfirmPassword}
                edge="end"
                sx={{ color: showConfirmPassword ? 'primary.main' : 'action.active' }}
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          sx={{ 
            py: 1.8, 
            borderRadius: 50,
            fontWeight: 600,
            fontSize: '1rem',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 10px rgba(66, 133, 244, 0.3)',
            '&:hover': {
              boxShadow: '0 6px 15px rgba(66, 133, 244, 0.4)',
              transform: 'translateY(-2px)',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: '0 2px 5px rgba(66, 133, 244, 0.3)',
            }
          }}
        >
          Tiếp tục
        </Button>
      </Box>

      {!isMobile && (
        <Fade in={true} timeout={600}>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
            Bằng việc đăng ký, bạn đồng ý với các Điều khoản và Chính sách của Bookdi
          </Typography>
        </Fade>
      )}
    </Box>
  );
};

export default AccountForm; 